import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Popconfirm, message, Tag } from 'antd';
import { 
  DeleteOutlined, 
  EditOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  EyeOutlined,
  ShareAltOutlined,
  FireOutlined,
  CommentOutlined
} from '@ant-design/icons';
import './index.css';

function PostCard({ post, isAuthor = false, onDelete, onEdit, compact = false }) {
  const [deleting, setDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const navigate = useNavigate();

  // 处理删除文章
  const handleDelete = async () => {
    if (!onDelete || !post.id) return;
    
    setDeleting(true);
    try {
      await onDelete(post.id);
      message.success('文章删除成功');
    } catch (error) {
      console.error('删除文章失败:', error);
      message.error('删除文章失败');
    } finally {
      setDeleting(false);
    }
  };

  // 处理编辑文章
  const handleEdit = () => {
    if (onEdit) {
      onEdit(post);
    } else {
      navigate(`/edit/${post.id}`);
    }
  };

  // 格式化日期
  const formatDate = (dateStr) => {
    if (!dateStr) return '未知日期';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return '今天';
      if (diffDays === 1) return '昨天';
      if (diffDays <= 7) return `${diffDays}天前`;
      
      return date.toLocaleDateString('zh-CN', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // 获取完整日期
  const getFullDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // 获取分类颜色
  const getCategoryColor = (category) => {
    const colors = {
      '前端': '#3b82f6',
      '技术': '#10b981',
      '编程': '#8b5cf6',
      '教程': '#f59e0b',
      'JavaScript': '#ef4444',
      'React': '#06b6d4',
      'Node.js': '#84cc16',
      'TypeScript': '#3b82f6',
      'CSS': '#ec4899',
      'Vue': '#10b981',
      '生活': '#8b5cf6',
      '思考': '#f59e0b',
      '读书': '#ef4444',
      '旅行': '#06b6d4',
      '默认': '#64748b'
    };
    return colors[category] || colors['默认'];
  };

  // 分享文章
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.origin + `/post/${post.id}`
      });
    } else {
      navigator.clipboard.writeText(window.location.origin + `/post/${post.id}`)
        .then(() => message.success('链接已复制到剪贴板'));
    }
  };

  // 如果没有文章数据，显示占位符
  if (!post) {
    return (
      <div className="post-card skeleton">
        <div className="post-image-container skeleton-image"></div>
        <div className="post-content">
          <div className="skeleton-title"></div>
          <div className="skeleton-excerpt"></div>
          <div className="skeleton-footer"></div>
        </div>
      </div>
    );
  }

  // 紧凑模式
  if (compact) {
    return (
      <div className="post-card compact">
        <div className="compact-content">
          <h4 className="compact-title">
            <Link to={`/post/${post.id}`}>
              {post.title || '无标题文章'}
            </Link>
          </h4>
          <div className="compact-meta">
            <span className="meta-item">
              <CalendarOutlined />
              {formatDate(post.date)}
            </span>
            {post.views > 0 && (
              <span className="meta-item">
                <EyeOutlined />
                {post.views}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 完整模式
  return (
    <div 
      className="post-card"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* 卡片头部 - 图片区域 */}
      <div className="post-image-container">
        <Link to={`/post/${post.id}`} className="post-image-link">
          <div className="post-image">
            {post.image ? (
              <img 
                src={post.image} 
                alt={post.title || '文章图片'} 
                loading="lazy"
              />
            ) : (
              <div className="image-placeholder">
                <div className="placeholder-icon">📝</div>
                <div className="placeholder-title">{post.title?.charAt(0) || 'B'}</div>
              </div>
            )}
            <div className="post-image-overlay"></div>
          </div>
        </Link>
        
        {/* 分类标签 */}
        {post.category && (
          <div 
            className="post-category-badge"
            style={{ backgroundColor: getCategoryColor(post.category) }}
          >
            {post.category}
          </div>
        )}

        {/* 热门标签 */}
        {post.views > 1000 && (
          <div className="post-hot-badge">
            <FireOutlined />
            热门
          </div>
        )}

        {/* 操作按钮（悬停显示，仅作者可见） */}
        {isAuthor && showActions && (
          <div className="post-actions-overlay">
            <div className="actions-buttons">
              <Popconfirm
                title="确定要删除这篇文章吗？"
                description="删除后无法恢复，请谨慎操作！"
                onConfirm={handleDelete}
                okText="确定删除"
                cancelText="取消"
                okType="danger"
              >
                <button 
                  className="action-btn delete-action"
                  disabled={deleting}
                >
                  <DeleteOutlined />
                  <span className="action-text">{deleting ? '删除中...' : '删除'}</span>
                </button>
              </Popconfirm>
              
              <button 
                className="action-btn edit-action"
                onClick={handleEdit}
              >
                <EditOutlined />
                <span className="action-text">编辑</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 卡片内容 */}
      <div className="post-content">
        {/* 元信息 */}
        <div className="post-meta-info">
          <div className="meta-group">
            <span className="meta-item" title={getFullDate(post.date)}>
              <CalendarOutlined />
              <span className="meta-text">{formatDate(post.date)}</span>
            </span>
            <span className="meta-item">
              <ClockCircleOutlined />
              <span className="meta-text">{post.readTime || '5'}分钟阅读</span>
            </span>
          </div>
          <div className="meta-group">
            {post.views > 0 && (
              <span className="meta-item">
                <EyeOutlined />
                <span className="meta-text">{post.views}次阅读</span>
              </span>
            )}
            {post.comments > 0 && (
              <span className="meta-item">
                <CommentOutlined />
                <span className="meta-text">{post.comments}条评论</span>
              </span>
            )}
          </div>
        </div>

        {/* 标题 */}
        <h3 className="post-title">
          <Link to={`/post/${post.id}`} className="title-link">
            {post.title || '无标题文章'}
          </Link>
        </h3>

        {/* 摘要 */}
        <p className="post-excerpt">
          {post.excerpt || post.summary || post.content?.substring(0, 120) || '暂无文章摘要...'}
          {(post.content?.length > 120 || post.excerpt?.length > 120) && '...'}
        </p>

        {/* 作者和标签区域 */}
        <div className="post-footer">
          <div className="author-info">
            <div className="author-avatar">
              {post.authorAvatar ? (
                <img src={post.authorAvatar} alt={post.author} />
              ) : (
                <UserOutlined />
              )}
            </div>
            <div className="author-details">
              <span className="author-name">{post.author || '匿名作者'}</span>
              <span className="author-bio" title={post.authorBio}>
                {post.authorBio ? post.authorBio.substring(0, 20) : '博主'}
              </span>
            </div>
          </div>

          {/* 标签 */}
          {post.tags && post.tags.length > 0 && (
            <div className="post-tags-container">
              <div className="tags-scroll">
                {post.tags.slice(0, 3).map((tag, index) => (
                  <span 
                    key={index} 
                    className="post-tag"
                    style={{ 
                      backgroundColor: `${getCategoryColor(tag)}15`,
                      color: getCategoryColor(tag),
                      borderColor: getCategoryColor(tag)
                    }}
                    title={`查看所有关于 #${tag} 的文章`}
                  >
                    #{tag}
                  </span>
                ))}
                {post.tags.length > 3 && (
                  <span className="post-tag more-tags" title={`还有${post.tags.length - 3}个标签`}>
                    +{post.tags.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="post-actions">
          <button className="action-button share-button" onClick={handleShare} title="分享文章">
            <ShareAltOutlined />
            <span>分享</span>
          </button>
          
          <Link to={`/post/${post.id}`} className="read-more-btn">
            阅读全文
            <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PostCard;